/**
 * GTM Lead Engine — request schemas for the admin-gtm routes.
 *
 * Validates dashboard → server request bodies/queries. Response shapes live in
 * types/gtm.ts (they come from our own DB, not untrusted input).
 */

import { z } from 'zod';

export const GtmLeadStageSchema = z.enum([
  'new',
  'enriched',
  'drafted',
  'queued',
  'sent',
  'replied',
  'demo_booked',
  'won',
  'lost',
  'skipped',
]);

export const GtmTierSchema = z.enum(['A', 'B', 'C', 'D']);

export const GtmChannelSchema = z.enum(['linkedin', 'twitter', 'email']);

/** Vendor-agnostic ICP search query — each connector maps it to its own API. */
export const GtmProspectQuerySchema = z.object({
  titles: z.array(z.string().min(1)).max(10).optional(),
  seniorities: z.array(z.string().min(1)).max(10).optional(),
  industries: z.array(z.string().min(1)).max(10).optional(),
  locations: z.array(z.string().min(1)).max(10).optional(),
  companySizes: z.array(z.string().min(1)).max(10).optional(),
  keywords: z.string().max(200).optional(),
  // Bounded page size so one click can't burn a shared vendor quota.
  perPage: z.number().int().min(1).max(25).default(25),
});
/**
 * maxResults/startPage are PRESET-ONLY pagination controls, deliberately NOT
 * in the schema: the public import route must stay bounded to one ≤25-row
 * page per click (the quota guardrail), so a request body carrying them is
 * silently stripped. The ICP batch runner sets them in code.
 */
export type GtmProspectQuery = z.infer<typeof GtmProspectQuerySchema> & {
  /** Paginated total target (connector loops pages). Unset → single perPage page. */
  maxResults?: number;
  /** 1-based page to resume from (persisted cursor). Unset → page 1. */
  startPage?: number;
};

/** POST /api/admin/gtm/imports — run a prospecting connector. */
export const GtmImportRequestSchema = GtmProspectQuerySchema.extend({
  /** Prospecting connector slug (e.g. 'apollo'). */
  connector: z.string().min(1).max(40),
});
export type GtmImportRequest = z.infer<typeof GtmImportRequestSchema>;

/** One manually-supplied prospect row (CSV paste / zero-vendor path). */
export const GtmManualRowSchema = z.object({
  fullName: z.string().min(1).max(200),
  title: z.string().max(200).optional(),
  company: z.string().max(200).optional(),
  email: z.string().email().optional(),
  linkedinUrl: z.string().url().optional(),
  twitterUrl: z.string().url().optional(),
  location: z.string().max(200).optional(),
  industry: z.string().max(120).optional(),
  seniority: z.string().max(60).optional(),
  companySize: z.number().int().positive().optional(),
});
export type GtmManualRow = z.infer<typeof GtmManualRowSchema>;

/** POST /api/admin/gtm/imports/manual — paste/CSV import, no vendor needed. */
export const GtmManualImportRequestSchema = z.object({
  rows: z.array(GtmManualRowSchema).min(1).max(200),
});
export type GtmManualImportRequest = z.infer<typeof GtmManualImportRequestSchema>;

/** GET /api/admin/gtm/leads query params. */
export const GtmLeadListQuerySchema = z.object({
  stage: GtmLeadStageSchema.optional(),
  tier: GtmTierSchema.optional(),
  /**
   * Sendability filter, independent of stage: 'yes' = has an email address,
   * 'no' = does not. Stage 'enriched' does NOT imply an email exists (a lead
   * whose enrichment found no match is still marked enriched), which is how
   * drafting effort got spent on unsendable leads.
   */
  hasEmail: z.enum(['yes', 'no']).optional(),
  search: z.string().max(200).optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type GtmLeadListQuery = z.infer<typeof GtmLeadListQuerySchema>;

/** POST /api/admin/gtm/leads/:id/draft */
export const GtmDraftRequestSchema = z.object({
  channel: GtmChannelSchema,
});
export type GtmDraftRequest = z.infer<typeof GtmDraftRequestSchema>;

/**
 * POST /api/admin/gtm/leads/draft-bulk(/preview) — filter-wide bulk drafting.
 * Scope is the caller's CURRENT list filters (same fields/semantics as the
 * lead list query), not a checkbox selection — "what I see is what drafts".
 * Channel is pinned to email for now; `limit` can only lower the server's
 * hard per-run cap, never raise it.
 */
export const GtmBulkDraftRequestSchema = GtmLeadListQuerySchema.pick({
  stage: true,
  tier: true,
  hasEmail: true,
  search: true,
}).extend({
  channel: z.literal('email').default('email'),
  limit: z.number().int().min(1).max(300).optional(),
});
export type GtmBulkDraftRequest = z.infer<typeof GtmBulkDraftRequestSchema>;

/** POST /api/admin/gtm/leads/:id/send */
export const GtmSendRequestSchema = z.object({
  messageId: z.string().uuid(),
});
export type GtmSendRequest = z.infer<typeof GtmSendRequestSchema>;

/** POST /api/admin/gtm/leads/export — selected-lead CSV export. */
export const GtmLeadExportRequestSchema = z.object({
  leadIds: z.array(z.string().uuid()).min(1).max(500),
});
export type GtmLeadExportRequest = z.infer<typeof GtmLeadExportRequestSchema>;

/** POST /api/admin/gtm/leads/bulk-enrich — Apollo bulk_match, ≤100 leads per call. */
export const GtmBulkEnrichRequestSchema = z.object({
  leadIds: z.array(z.string().uuid()).min(1).max(100),
  /** Re-enrich leads that already have an email (default: skip them). */
  force: z.boolean().optional(),
});
export type GtmBulkEnrichRequest = z.infer<typeof GtmBulkEnrichRequestSchema>;

/** POST /api/admin/gtm/leads/waterfall — Apollo waterfall email enrichment, ≤25 leads per run. */
export const GtmWaterfallRequestSchema = z.object({
  leadIds: z.array(z.string().uuid()).min(1).max(25),
});
export type GtmWaterfallRequest = z.infer<typeof GtmWaterfallRequestSchema>;

export const GtmSubSegmentSchema = z.enum([
  'stack_hopping_solopreneur',
  'automation_agency_operator',
  'ai_educator_creator',
  'ai_forward_employee',
  'marketing_agency_founder',
  'd2c_ecommerce_operator',
  'bookkeeping_accounting_owner',
  'nontechnical_founder',
  'brokerage_real_estate',
  'clinic_wellness_owner',
  'unclassified',
]);

export const GtmDesignPartnerStatusSchema = z.enum(['prospect', 'active', 'dropped']);

/** POST /api/admin/gtm/pmf/partners — create a design partner. */
export const GtmDesignPartnerUpsertSchema = z.object({
  name: z.string().min(1).max(200),
  company: z.string().max(200).nullish(),
  subSegment: GtmSubSegmentSchema.default('unclassified'),
  status: GtmDesignPartnerStatusSchema.default('prospect'),
  hitAha: z.boolean().default(false),
  notes: z.string().max(5000).nullish(),
});

/** PATCH /api/admin/gtm/pmf/partners/:id — partial update (weekly check-in). */
export const GtmDesignPartnerPatchSchema = z
  .object({
    name: z.string().min(1).max(200),
    company: z.string().max(200).nullish(),
    subSegment: GtmSubSegmentSchema,
    status: GtmDesignPartnerStatusSchema,
    hitAha: z.boolean(),
    notes: z.string().max(5000).nullish(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Provide at least one field to update' });

/** POST /api/admin/gtm/pmf/snapshot — record this week's gate readings (all optional). */
export const GtmPmfSnapshotUpsertSchema = z
  .object({
    seanEllisPct: z.number().min(0).max(100).nullish(),
    d7RetentionPct: z.number().min(0).max(100).nullish(),
    ahaPct: z.number().min(0).max(100).nullish(),
    waitlistActivePct: z.number().min(0).max(100).nullish(),
    tasksPerUserWeek: z.number().min(0).nullish(),
  })
  // Values, not keys: nullish fields keep their keys, and the board reads only
  // the newest row — an all-null save would blank every gate tile in one click.
  .refine((v) => Object.values(v).some((x) => x != null), {
    message: 'Provide at least one reading',
  });

/** PATCH /api/admin/gtm/leads/:id — manual stage transition and/or sub-segment override. */
export const GtmLeadPatchSchema = z
  .object({
    stage: GtmLeadStageSchema.optional(),
    subSegment: GtmSubSegmentSchema.optional(),
  })
  .refine((v) => v.stage !== undefined || v.subSegment !== undefined, {
    message: 'Provide a stage and/or a subSegment to update',
  });
export type GtmLeadPatch = z.infer<typeof GtmLeadPatchSchema>;

// =============================================================================
// Warm-lead loop — engagement import
// =============================================================================

export const GtmSignalTypeSchema = z.enum([
  'magnet_request',
  'replied',
  'commented',
  'liked',
  'followed',
]);

export const GtmSignalPlatformSchema = z.enum(['linkedin', 'x']);

/** One scraped engagement row. Profile URL is the identity/dedupe key. */
export const GtmEngagementRowSchema = z.object({
  fullName: z.string().min(1).max(200),
  profileUrl: z.string().url(),
  title: z.string().max(200).optional(),
  company: z.string().max(200).optional(),
  email: z.string().email().optional(),
});

/** POST /api/admin/gtm/imports/engagement — one batch = one post × one signal type. */
export const GtmEngagementImportRequestSchema = z.object({
  platform: GtmSignalPlatformSchema,
  signalType: GtmSignalTypeSchema,
  /** The post the engagement came from (URL or id). */
  postRef: z.string().max(500).optional(),
  rows: z.array(GtmEngagementRowSchema).min(1).max(500),
});
export type GtmEngagementRow = z.infer<typeof GtmEngagementRowSchema>;
export type GtmEngagementImportRequest = z.infer<typeof GtmEngagementImportRequestSchema>;

// =============================================================================
// Drip sequences (m2)
// =============================================================================

export const GtmSequenceStepSchema = z.object({
  channel: GtmChannelSchema,
  delayDays: z.number().int().min(0).max(90),
  note: z.string().max(300).optional(),
});

/** POST/PUT /api/admin/gtm/sequences — sequence definition. */
export const GtmSequenceUpsertSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
  steps: z.array(GtmSequenceStepSchema).min(1).max(10),
  isActive: z.boolean().default(true),
});
export type GtmSequenceUpsert = z.infer<typeof GtmSequenceUpsertSchema>;

/** POST /api/admin/gtm/sequences/:id/enroll — enroll leads. */
export const GtmEnrollRequestSchema = z.object({
  leadIds: z.array(z.string().uuid()).min(1).max(100),
});
export type GtmEnrollRequest = z.infer<typeof GtmEnrollRequestSchema>;

/** POST /api/admin/gtm/enrollments/:id/(pause|resume|stop) */
export const GtmEnrollmentActionSchema = z.object({
  reason: z.string().max(200).optional(),
});
export type GtmEnrollmentAction = z.infer<typeof GtmEnrollmentActionSchema>;

// =============================================================================
// GTM Ops board
// =============================================================================

export const GtmLaunchUpsertSchema = z.object({
  title: z.string().min(1).max(200),
  hook: z.string().max(300).optional(),
  launchDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  status: z.enum(['idea', 'prepping', 'launched', 'relaunch']).default('idea'),
  channel: z.string().max(60).optional(),
  notes: z.string().max(1000).optional(),
});
export type GtmLaunchUpsert = z.infer<typeof GtmLaunchUpsertSchema>;

export const GtmDirectoryPatchSchema = z.object({
  status: z.enum(['todo', 'submitted', 'listed', 'rejected']),
  notes: z.string().max(500).optional(),
});
export type GtmDirectoryPatch = z.infer<typeof GtmDirectoryPatchSchema>;

/** POST /api/admin/gtm/ops/weekly/toggle */
export const GtmWeeklyToggleSchema = z.object({
  week: z.string().regex(/^\d{4}-W\d{2}$/),
  itemKey: z.string().min(1).max(60),
  done: z.boolean(),
});
export type GtmWeeklyToggle = z.infer<typeof GtmWeeklyToggleSchema>;

// =============================================================================
// GTM Discovery — AI discovery platforms catalog + listing profile
// =============================================================================

export const GtmDiscoveryCategorySchema = z.enum(['ai_directory', 'launch_platform', 'review_site', 'general']);
export const GtmDiscoveryPricingModelSchema = z.enum(['free', 'freemium', 'paid']);

const GtmDirectoryRequirementsSchema = z
  .object({
    needsAccount: z.boolean().nullable().optional(),
    taglineMax: z.number().int().positive().nullable().optional(),
    descMax: z.number().int().positive().nullable().optional(),
    logoSpec: z.string().max(300).nullable().optional(),
    reviewDays: z.string().max(120).nullable().optional(),
    notes: z.string().max(1000).nullable().optional(),
  })
  .strict();

const discoveryEditableFields = {
  url: z.string().url().max(500).nullable().optional(),
  category: GtmDiscoveryCategorySchema.optional(),
  pricingModel: GtmDiscoveryPricingModelSchema.nullable().optional(),
  priceUsd: z.number().nonnegative().max(100000).nullable().optional(),
  pricingNotes: z.string().max(1000).nullable().optional(),
  trafficEstimate: z.number().int().nonnegative().nullable().optional(),
  domainRating: z.number().int().min(0).max(100).nullable().optional(),
  submitUrl: z.string().url().max(600).nullable().optional(),
  listingUrl: z.string().url().max(600).nullable().optional(),
  requirements: GtmDirectoryRequirementsSchema.nullable().optional(),
  researchNotes: z.string().max(2000).nullable().optional(),
  notes: z.string().max(500).nullable().optional(),
};

/** POST /api/admin/gtm/discovery/platforms */
export const GtmDiscoveryPlatformCreateSchema = z.object({
  name: z.string().min(1).max(120),
  ...discoveryEditableFields,
});
export type GtmDiscoveryPlatformCreate = z.infer<typeof GtmDiscoveryPlatformCreateSchema>;

/** PATCH /api/admin/gtm/discovery/platforms/:id */
export const GtmDiscoveryPlatformPatchSchema = z.object({
  status: z.enum(['todo', 'submitted', 'listed', 'rejected']).optional(),
  /** Set when a research proposal is applied — stamps last_researched_at. */
  researched: z.boolean().optional(),
  ...discoveryEditableFields,
});
export type GtmDiscoveryPlatformPatch = z.infer<typeof GtmDiscoveryPlatformPatchSchema>;

/** PUT /api/admin/gtm/discovery/profile */
export const GtmListingProfileUpsertSchema = z.object({
  productName: z.string().min(1).max(120),
  tagline: z.string().min(1).max(300),
  descriptionShort: z.string().max(500),
  descriptionMedium: z.string().max(2000),
  descriptionLong: z.string().max(8000),
  categoryTags: z.array(z.string().max(50)).max(20),
  websiteUrl: z.string().url().max(300),
  logoUrl: z.string().url().max(600).nullable().optional(),
  screenshotUrls: z.array(z.string().url().max(600)).max(10).optional().default([]),
  videoUrl: z.string().url().max(600).nullable().optional(),
  contactEmail: z.string().email().max(200).nullable().optional(),
  socialLinks: z.record(z.string(), z.string().max(600)).nullable().optional(),
  pricingBlurb: z.string().max(1000).nullable().optional(),
});
export type GtmListingProfileUpsert = z.infer<typeof GtmListingProfileUpsertSchema>;

/** POST /api/admin/gtm/discovery/research — refresh one platform OR discover new ones. */
export const GtmDiscoveryResearchRequestSchema = z
  .object({
    platformId: z.string().uuid().optional(),
    discover: z.boolean().optional(),
  })
  .refine((v) => Boolean(v.platformId) !== Boolean(v.discover), {
    message: 'Provide exactly one of platformId or discover',
  });
export type GtmDiscoveryResearchRequest = z.infer<typeof GtmDiscoveryResearchRequestSchema>;

// =============================================================================
// Content factory
// =============================================================================

export const GtmContentPlatformSchema = z.enum([
  'x',
  'instagram',
  'linkedin',
  'tiktok',
  'youtube',
  'other',
]);

/** POST /api/admin/gtm/content/snipes — submit a viral-post link. */
export const GtmSnipeSubmitSchema = z.object({
  url: z.string().url().max(600),
  /** Post text pasted by the operator (platforms block server-side scraping). */
  pastedContent: z.string().max(6000).optional(),
  metrics: z
    .object({
      views: z.number().int().nonnegative().optional(),
      likes: z.number().int().nonnegative().optional(),
      comments: z.number().int().nonnegative().optional(),
      shares: z.number().int().nonnegative().optional(),
    })
    .optional(),
  notes: z.string().max(500).optional(),
});
export type GtmSnipeSubmit = z.infer<typeof GtmSnipeSubmitSchema>;

/** PATCH /api/admin/gtm/content/snipes/:id — status / calendar planning. */
export const GtmSnipePatchSchema = z.object({
  status: z.enum(['new', 'analyzed', 'planned', 'used', 'discarded']).optional(),
  plannedDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  plannedChannel: z.string().max(60).nullable().optional(),
  notes: z.string().max(500).nullable().optional(),
});
export type GtmSnipePatch = z.infer<typeof GtmSnipePatchSchema>;

/** POST/PUT /api/admin/gtm/magnets — lead-magnet registry. */
export const GtmLeadMagnetUpsertSchema = z.object({
  title: z.string().min(1).max(200),
  hook: z.string().max(300).optional(),
  assetUrl: z.string().url().max(600).optional(),
  isActive: z.boolean().default(false),
});
export type GtmLeadMagnetUpsert = z.infer<typeof GtmLeadMagnetUpsertSchema>;

/** PATCH /api/admin/gtm/content/influencers/:id */
export const GtmInfluencerPatchSchema = z.object({
  status: z
    .enum(['watch', 'shortlisted', 'contacted', 'negotiating', 'active', 'rejected'])
    .optional(),
  followers: z.number().int().nonnegative().nullable().optional(),
  name: z.string().max(120).nullable().optional(),
  notes: z.string().max(1000).nullable().optional(),
});
export type GtmInfluencerPatch = z.infer<typeof GtmInfluencerPatchSchema>;
